import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, RendererFactory2, ViewChild, ViewEncapsulation } from '@angular/core';
import { Field } from 'app/interfaces/fields';
import { AngularNodeViewComponent } from 'ngx-tiptap';
import { GlobalService } from '../global.service';
import { onFieldValidation } from '../fieldvalidation';
import { applyRelations } from '../fieldrelation';
import { DocumentFieldEmitterService } from '../documentfield-emitter.service';
import { EditorEmitterService } from 'app/main/pages/events/editor-emitter.service';
import { debounceTime, lastValueFrom, Observable, Subject, takeUntil, take } from 'rxjs';
import { MomentPipe } from 'app/pipes/moment.pipe';
import { TippyWrapperInstance } from '../tippy-wrapper.service';
import flatpickr from 'flatpickr';
import { clearButtonForNg } from '../flatpickerButtonPlugin';
import { DomHandlersService } from '../dom-handlers.service';
import { EditTemplateFacade } from 'app/main/pages/edit-template/store/edit-template-store.facade';
import { applyValueToField } from '../synchronize';
import { ValidationHandlerService } from '../validation-handler/validation-handler.service';
import { generateCalculation } from '../calculation';

@Component({
  selector: 'app-tag-popover',
  templateUrl: './tag-popover.component.html',
  styleUrl: './tag-popover.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagPopoverComponent extends AngularNodeViewComponent implements OnDestroy, OnInit, AfterViewInit {
  @Input()
  get _fieldData(): Field {
    return this.fieldData;
  }
  set _fieldData(value: Field) {
    this.fieldData = value || null;
  }
  @Input()
  get _role_id(): number {
    return this.role_id;
  }
  set _role_id(value: number) {
    this.role_id = value;
  }
  @Input()
  get hide_settings(): boolean {
    return this.hide_setting_option;
  }
  set hide_settings(value: boolean) {
    this.hide_setting_option = value || false;
  }
  @Input()
  get tippy(): TippyWrapperInstance {
    return this.tippyInstance;
  }
  set tippy(value: TippyWrapperInstance) {
    this.tippyInstance = value;
  }
  @ViewChild('textInput')
  textInput!: ElementRef<HTMLInputElement>;
  @ViewChild('multilineInput')
  multilineInput!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('dateInput')
  dateInput!: ElementRef<HTMLInputElement>;
  @ViewChild('droplistSelect')
  droplistSelect?: ElementRef;
  private renderer: Renderer2;
  public hide_setting_option: boolean;
  public fieldData: Field;
  public role_id: number;
  public tippyInstance: TippyWrapperInstance;
  private inputUpdate = new Subject<{$event: InputEvent, fieldData:Field}>();
  private destroyed = new Subject<void>();
  public clearButton:HTMLElement = document.createElement('button');
  public editMode:boolean = false;
  public canEdit:boolean = true;
  constructor(
    private globalService: GlobalService,
    private emitter: DocumentFieldEmitterService,
    private editorEmitterService: EditorEmitterService,
    private _momentPipe$: MomentPipe,
    private domHandlersService: DomHandlersService,
    rendererFactory: RendererFactory2,
    private editTemplateFacade: EditTemplateFacade,
    private validationHandlerService: ValidationHandlerService
  ) {
    super();
    this.renderer = rendererFactory.createRenderer(null, null);
  }
  public flatpickrDefaultInterFace = {
    monthSelectorType: 'dropdown',
  }
  
  ngOnInit(): void {
    this.inputUpdate.pipe(debounceTime(300), takeUntil(this.destroyed)).subscribe(({$event, fieldData}) => {
        this.syncTextfield($event, fieldData);
    });
    this.editMode = document.querySelector('.edit-template') ? true : false;
    this.editTemplateFacade.template$.pipe(take(1)).subscribe((template) => {
        if(template?.template?.can_edit === false && this.fieldData?.is_locked === 1) {
          this.canEdit = false;
        }
    })
  }

  ngAfterViewInit(): void {
    // Auto-focus the input field for better UX
    // Determine which input to focus based on field type
    if (this.fieldData?.field === 'text-field') {
      if (this.fieldData?.allow_multi_lines === 1 && this.multilineInput) {
        // Focus textarea for multi-line text
        setTimeout(() => this.multilineInput?.nativeElement?.focus(), 0);
      } else if (this.textInput) {
        // Focus input for single-line text
        setTimeout(() => this.textInput?.nativeElement?.focus(), 0);
      }
    } else if (this.fieldData?.field === 'datepicker-field' && this.dateInput) {
      // Focus datepicker input
      setTimeout(() => this.dateInput?.nativeElement?.focus(), 0);
    } else if (this.fieldData?.field === 'droplist-field' && this.droplistSelect) {
      // Focus ng-select droplist
      setTimeout(() => this.droplistSelect?.nativeElement?.focus(), 0);
    }
  }

  textUpdate($event: InputEvent, fieldData:Field){
    this.inputUpdate.next({$event, fieldData});
  }

  syncTextfield($event: InputEvent, fieldData:Field) {
    if (Number(fieldData.selected_for_role_id) === this.role_id || (fieldData.selected_for_role_id === -1 || this.editMode)) {
      const value = ($event.target as HTMLInputElement)?.value;
      applyValueToField(fieldData.uuid, value, this.globalService, true);
      const elementContainer = document.querySelector('.drag-fields[uuid="' + fieldData.uuid + '"]')
      const inputElement = elementContainer?.querySelector('input');
      this.globalService.updateField(fieldData.uuid, { value: value }, (field: Field) => {
        onFieldValidation(field, this.globalService, field.selected_for_role_id);
        applyRelations(fieldData.uuid, this.globalService);
        this.emitter.patchData({ uuid: fieldData.uuid, value: value, refresh: 1 });
        if (inputElement && inputElement.classList.contains('calculation')) {
            generateCalculation(fieldData.uuid, this.globalService, true);
        }
        if($event['key'] === "Enter" && fieldData.allow_multi_lines !== 1){
          this.tippyInstance.destroy();
        }
      });
      this.validationHandlerService.resetFieldHighlight(fieldData?.uuid);
    }
  }

  async syncDatepickerfield($event: InputEvent, fieldData: Field) {
    if (Number(fieldData.selected_for_role_id) === this.role_id || (fieldData.selected_for_role_id === -1 || this.editMode)) {
      const value = ($event.target as HTMLInputElement)?.value as string;
      const formatedDate = (value ? await lastValueFrom(this._momentPipe$.transform(value) as Observable<string>) : "");
      const datepickerElement = document.querySelector(`.drag-fields[uuid="${fieldData.uuid}"]`);
      if (datepickerElement) {
        datepickerElement.querySelector('.tag-date').textContent = value ? formatedDate : this.globalService.generateTagText(fieldData.uuid);
      }
      this.globalService.updateField(fieldData.uuid, { value: value, alt_value: formatedDate });
      applyRelations(fieldData.uuid, this.globalService);
      this.emitter.patchData({ uuid: fieldData.uuid, value: value, refresh: 1 });
      this.tippyInstance.destroy();
      this.validationHandlerService.resetFieldHighlight(fieldData?.uuid);
    }
  }

  syncDroplistField(option: string | string[], fieldData: Field) {
    if (Number(fieldData.selected_for_role_id) === this.role_id || (fieldData.selected_for_role_id === -1 || this.editMode)) {
      if(fieldData?.multi_select === 1){
        const isEmptyOptionSelect = (option as string[]).some(x => x === '');
        if(isEmptyOptionSelect){
          (option as string[]) = [];
        }
      }
      let selectedValue: string | string[] | null = null;
      let selectedOptionName: string | string[] | null = null;
      let selectedOptionId: number | number[] | null = null;

      if (fieldData?.multi_select === 1) {
          const { selectedValue: value, selectedNames: name, selectedOptionid: id } = this.globalService.refactorDroplistValue(option as string[], fieldData) ?? {};
          selectedValue = value ?? null;
          selectedOptionName = name ?? null;
          selectedOptionId = id ?? null;
      }else{
        const selectedOption = fieldData.options.find(opt => opt.value == option);
        if (selectedOption) {
          const foundDroplist = this.globalService.findDroplistById(fieldData.uuid, selectedOption.id);

          selectedValue = foundDroplist?.value ?? null;
          selectedOptionName = foundDroplist?.name ?? null;
          selectedOptionId = foundDroplist?.id ?? null;
        }
      }
      const selecedOptionLabel = document.querySelector(`.drag-fields[uuid='${fieldData.uuid}']`);
      selecedOptionLabel.querySelectorAll('.selected-option-label').forEach((droplist: HTMLElement) => {
        this.renderer.setProperty(droplist, 'textContent', (fieldData.tag_view == "1" && (!option || !option?.length)) ? this.globalService.generateTagText(fieldData.uuid) : selectedOptionName)
      });
      this.globalService.updateField(fieldData.uuid, { value: selectedValue, selected_option_id: selectedOptionId }, () => {
        applyRelations(fieldData.uuid, this.globalService);
        this.emitter.patchData({ uuid: fieldData.uuid, value: selectedValue, refresh: 1 });
      });
      if(fieldData?.multi_select !== 1){
        this.tippyInstance.destroy();
      }
      this.validationHandlerService.resetFieldHighlight(fieldData?.uuid);
    }
  }

  openSetting() {
    // Defensive guards: ensure we have fieldData and uuid
    if (!this.fieldData) {
      console.warn('[TagPopover] openSetting called but fieldData is null/undefined');
      return;
    }
    if (!this.fieldData?.uuid) {
      console.warn('[TagPopover] openSetting called but fieldData.uuid is missing');
      return;
    }
    this.editorEmitterService.triggerEmitter({
      uuid: this.fieldData.uuid,
      field_name: this.fieldData['field_name'],
      selected_field: this.fieldData['selected_field'],
      isFormBuilder: 0,
      formUuid: 0,
      isFieldBlock: Number(this.fieldData['field_block']),
      autoOpenSettings: true,
      forcelyOpenSetting: true
    });
    // Destroy tippy instance only if present and has destroy method
    if (this.tippyInstance && typeof this.tippyInstance.destroy === 'function') {
      this.tippyInstance.destroy();
    }
  }

  onReadyDatepicker({instance}:{instance:flatpickr.Instance}) {
    clearButtonForNg(this.clearButton, instance);
  }

  getSuggestions(event: KeyboardEvent | MouseEvent, field: Field){
    if(field?.api_field){
      const input = document.querySelector(`.tags-input[uuid="${field.uuid}"]`) as HTMLInputElement;
      if (!input) return;
      this.domHandlersService.onTextFieldFocus(event, {role_id: 0}, field.uuid, input, 'tagPopoverField');  
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next()
    this.destroyed.complete()
  }
}

